import { useCallback, useMemo, useState } from "react";

import { Button } from "V2.0/common/button";
import { Dialog, DialogTitle, DialogBody } from "V2.0/common/dialog";
import { DialogTitleWithClose } from "V2.0/components/DialogTitleWithClose";
import { TTSSettings } from "./TTSSettings";
import { ChevronDownIcon, Cog6ToothIcon } from "@heroicons/react/20/solid";
import { TTSProviderSettings } from "../TTSProviderSettings/TTSProviderSettings";
import { MicrophoneIcon } from "@heroicons/react/24/solid";
import { basicVoices, basicPlusVoices, proVoices, premiumVoices, superdashVoices, VoiceProvider } from "./voices";

function TTS(props) {
  const {
    settings,
    syncAssistant,
    assistantId,
    workspaceId,
    voices,
    locked,
    showPremiumOptions,
  } = props;
  const [open, setOpen] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const inputField = useMemo(() => {
    if (settings?.voiceId) {
      if (settings?.voiceId === "Cindy" || settings?.voiceId === "Arthor") {
        return { voiceId: "Eva" };
      }

      return { voiceId: settings?.voiceId };
    }
    return { voiceId: "" };
  }, [settings]);

  const { proCustomVoices, premiumCustomVoices, superdashCustomVoices } = useMemo(() => {
    return voices.reduce(
      (acc, voice) => {
        if (voice.voiceProvider === VoiceProvider.Elevenlabs) {
          return {
            ...acc,
            premiumCustomVoices: [...acc.premiumCustomVoices, voice],
          };
        }

        if (
          voice.voiceProvider === VoiceProvider.SuperdashTTS1 ||
          voice.voiceProvider === VoiceProvider.SuperdashTTS2
        ) {
          return {
            ...acc,
            superdashCustomVoices: [...acc.superdashCustomVoices, voice],
          };
        }

        return {
          ...acc,
          proCustomVoices: [...acc.proCustomVoices, voice],
        };
      },
      {
        proCustomVoices: [],
        premiumCustomVoices: [],
        superdashCustomVoices: [],
      }
    );
  }, [voices]);

  const updatedProVoices = useMemo(() => {
    const proCustomVoicesList = proCustomVoices.reduce((acc, voice) => {
      const { voiceName } = voice;

      return {
        ...acc,
        [voiceName]: { label: voiceName },
      };
    }, {});

    const updatedProVoices = {
      ...proVoices,
      ...proCustomVoicesList,
    };

    return updatedProVoices;
  }, [proCustomVoices]);

  const updatedPremiumVoices = useMemo(() => {
    const premiumVoicesList = premiumCustomVoices.reduce((acc, voice) => {
      const { voiceName } = voice;

      return {
        ...acc,
        [voiceName]: { label: voiceName },
      };
    }, {});

    const updatedPremiumVoices = {
      ...premiumVoices,
      ...premiumVoicesList,
    };

    return updatedPremiumVoices;
  }, [premiumCustomVoices]);

  const updatedSuperdashVoices = useMemo(() => {
    const superdashCustomVoicesList = superdashCustomVoices.reduce((acc, voice) => {
      const { voiceName } = voice;

      return {
        ...acc,
        [voiceName]: { label: voiceName },
      };
    }, {});

    const updatedSuperdashVoices = {
      ...superdashVoices,
      ...superdashCustomVoicesList,
    };

    return updatedSuperdashVoices;
  }, [superdashCustomVoices]);

  const voicesList = useMemo(() => {
    if (showPremiumOptions) {
      return {
        ...basicVoices,
        ...basicPlusVoices,
        ...updatedProVoices,
        ...updatedSuperdashVoices,
        ...updatedPremiumVoices,
      };
    }

    return {
      ...basicVoices,
      ...basicPlusVoices,
      ...updatedProVoices,
      ...updatedSuperdashVoices,
    };
  }, [updatedPremiumVoices, updatedProVoices, updatedSuperdashVoices, showPremiumOptions]);

  const voice = voicesList[inputField.voiceId]?.label ?? inputField.voiceId;

  const ttsServiceProvider = settings?.ttsServiceProvider;
  const showTTSSettings =
    ttsServiceProvider === "elevenlabs" ||
    ttsServiceProvider === "azuretts" ||
    ttsServiceProvider === "googletts" ||
    // ttsServiceProvider === "rimeai" ||
    ttsServiceProvider === "cartesia" ||
    // Loudness settings — omni (TTS2) voices only; TTS1/VoxCPM streams while
    // generating and has no loudness controls.
    ttsServiceProvider === "superdashtts2";

  return (
    <div className="flex items-center border rounded-lg min-h-10">
      <div className="flex items-center px-2 gap-x-2">
        <MicrophoneIcon className="size-4" />
        <p className="text-sm">{voice}</p>
      </div>
      {!locked && (
        <div className="border-r">
          <Button
            plain
            onClick={() => setOpen(true)}
          >
            <ChevronDownIcon />
          </Button>
        </div>
      )}
      {showTTSSettings && !locked && (
        <Button
          plain
          onClick={() => setOpenSettings(true)}
        >
          <Cog6ToothIcon />
        </Button>
      )}
      {open && (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
        >
          <DialogTitle>
            <DialogTitleWithClose
              title={"Select Voice"}
              onClose={() => setOpen(false)}
            />
          </DialogTitle>
          <DialogBody>
            <TTSSettings
              premiumVoices={updatedPremiumVoices}
              proVoices={updatedProVoices}
              superdashVoices={updatedSuperdashVoices}
              showPremiumOptions={showPremiumOptions}
              settings={inputField}
              syncAssistant={syncAssistant}
              assistantId={assistantId}
              workspaceId={workspaceId}
              onSuccess={() => setOpen(false)}
            />
          </DialogBody>
        </Dialog>
      )}
      {openSettings && (
        <Dialog
          open={openSettings}
          onClose={() => setOpenSettings(false)}
        >
          <DialogTitle>Voice Settings</DialogTitle>
          <DialogBody>
            {TTSProviderSettings[ttsServiceProvider]({
              ...settings,
              syncAssistant,
              assistantId,
              workspaceId,
              onClose: () => setOpenSettings(false),
            })}
          </DialogBody>
        </Dialog>
      )}
    </div>
  );
}

export { TTS };
